import React, { useState, useMemo, useRef } from 'react';
import { Button, Space, Card, Modal, Row, Col, Divider, Tooltip } from 'antd';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import numberToWords from 'number-to-words';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import TableContainer from 'components/common/TableContainer';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import nubaxLogo from '../../../../assets/img/nubaxlogo.png';
pdfMake.addVirtualFileSystem(pdfFonts);

pdfMake.vfs = pdfFonts.vfs;

pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

const PayslipUsers = ({ usersData, month, year }) => {
  const [searchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [show, setShow] = useState(false);
  const [viewUserId, setViewUserId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const payslipRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const displayValue = (value, fallback = '-') =>
    value !== null && value !== undefined && value !== '' ? value : fallback;

  const formatCurrency = (value) =>
    value != null && !isNaN(value)
      ? `Rs.${Number(value).toFixed(2)}`
      : 'Rs.0.00';

  const amountInWords = (amount) => {
    const num = Number(amount);
    if (!amount || isNaN(num)) return 'Zero';
    return numberToWords.toWords(num);
  };

  const getPayableAmount = (payslip) =>
    payslip?.Final_Amount ?? payslip?.net_pay ?? 0;

  const convertImageToBase64 = async (imgPath) => {
    const response = await fetch(imgPath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const generatePayslipPDF = async (payslip) => {
    try {
      setPdfLoading(true);
      const logoBase64 = await convertImageToBase64(nubaxLogo);

      const payslipMonth = payslip.Month || payslip.month;
      const payslipYear = payslip.Year || payslip.year;
      const payableAmount = getPayableAmount(payslip);

      // NOTE: NO font, bold, italics, or bolditalics anywhere in this definition.
      // Every text node is plain — pdfMake will use its default Roboto-Regular only.
      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 50, 40, 50],
        content: [
          // Logo
          {
            image: logoBase64,
            width: 90,
            alignment: 'center',
            margin: [0, 0, 0, 6],
          },

          // Header
          {
            text: displayValue(payslip.company_name),
            fontSize: 14,
            alignment: 'center',
            margin: [0, 4, 0, 2],
          },
          {
            text: displayValue(
              payslip.registered_office || payslip.corporate_office,
            ),
            fontSize: 10,
            alignment: 'center',
            margin: [0, 0, 0, 4],
          },
          {
            text: `Payslip for the Month of ${displayValue(payslipMonth)}, ${displayValue(payslipYear)}`,
            fontSize: 11,
            alignment: 'center',
            margin: [0, 6, 0, 10],
          },
          // Employee Info + Bank Details
          {
            table: {
              widths: ['50%', '50%'],
              body: [
                [
                  {
                    stack: [
                      {
                        table: {
                          widths: ['55%', '45%'],
                          body: [
                            [
                              {
                                text: 'Employee Info',
                                colSpan: 2,
                                fillColor: '#f5f5f5',
                                margin: [6, 4, 6, 4],
                              },
                              {},
                            ],
                            [
                              { text: 'Name', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.employee_name),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Designation', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.designations),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Department', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.department_name),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Location', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.work_location),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'LOP', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.lop),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0.5,
                          vLineWidth: () => 0,
                          hLineColor: () => '#cccccc',
                        },
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        table: {
                          widths: ['55%', '45%'],
                          body: [
                            [
                              {
                                text: 'Bank Details',
                                colSpan: 2,
                                fillColor: '#f5f5f5',
                                margin: [6, 4, 6, 4],
                              },
                              {},
                            ],
                            [
                              { text: 'Employee ID', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.employee_id),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Bank Name', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.bank_name),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Account No', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.account_number),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'PAN No', margin: [6, 3, 6, 3] },
                              {
                                text: displayValue(payslip.pan_no),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0.5,
                          vLineWidth: () => 0,
                          hLineColor: () => '#cccccc',
                        },
                      },
                    ],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
              paddingLeft: () => 0,
              paddingRight: () => 0,
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
            margin: [0, 0, 0, 10],
          },

          // Earnings + Deductions
          {
            table: {
              widths: ['50%', '50%'],
              body: [
                [
                  {
                    stack: [
                      {
                        table: {
                          widths: ['60%', '40%'],
                          body: [
                            [
                              {
                                text: 'Earnings',
                                colSpan: 2,
                                fillColor: '#f5f5f5',
                                margin: [6, 4, 6, 4],
                              },
                              {},
                            ],
                            [
                              { text: 'EL Encashment', margin: [6, 3, 6, 3] },
                              {
                                text: formatCurrency(
                                  payslip.EL_Encashment || 0.0,
                                ),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Basic', margin: [6, 3, 6, 3] },
                              {
                                text: formatCurrency(
                                  payslip.basic_month || 0.0,
                                ),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'HRA', margin: [6, 3, 6, 3] },
                              {
                                text: formatCurrency(payslip.hra_month || 0.0),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              {
                                text: 'Conveyance Allowance',
                                margin: [6, 3, 6, 3],
                              },
                              {
                                text: formatCurrency(
                                  payslip.conveyance_allowance_month || 0.0,
                                ),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              {
                                text: 'Special Allowance',
                                margin: [6, 3, 6, 3],
                              },
                              {
                                text: formatCurrency(
                                  payslip.special_allowance_month || 0.0,
                                ),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                            [
                              { text: 'Variable', margin: [6, 3, 6, 3] },
                              {
                                text: formatCurrency(payslip.variable || 0.0),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0.5,
                          vLineWidth: () => 0,
                          hLineColor: () => '#cccccc',
                        },
                      },
                    ],
                  },
                  {
                    stack: [
                      {
                        table: {
                          widths: ['60%', '40%'],
                          body: [
                            [
                              {
                                text: 'Deductions',
                                colSpan: 2,
                                fillColor: '#f5f5f5',
                                margin: [6, 4, 6, 4],
                              },
                              {},
                            ],
                            [
                              { text: 'Adoc', margin: [6, 3, 6, 3] },
                              {
                                text: formatCurrency(payslip.adoc_deduction),
                                margin: [6, 3, 6, 3],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0.5,
                          vLineWidth: () => 0,
                          hLineColor: () => '#cccccc',
                        },
                      },
                    ],
                  },
                ],
                [
                  {
                    colSpan: 2,
                    fillColor: '#fafafa',
                    columns: [
                      {
                        width: '50%',
                        columns: [
                          { text: 'Total Earnings:', margin: [8, 5, 0, 5] },
                          {
                            text: formatCurrency(payableAmount),
                            alignment: 'right',
                            margin: [0, 5, 8, 5],
                          },
                        ],
                      },
                      {
                        width: '50%',
                        columns: [
                          { text: 'Total Deductions:', margin: [8, 5, 0, 5] },
                          {
                            text: formatCurrency(payslip.adoc_deduction),
                            alignment: 'right',
                            margin: [0, 5, 8, 5],
                          },
                        ],
                      },
                    ],
                  },
                  {},
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
              paddingLeft: () => 0,
              paddingRight: () => 0,
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
            margin: [0, 0, 0, 10],
          },

          // Net Pay box
          {
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    stack: [
                      {
                        text: `Net Pay For The Month: ${formatCurrency(payableAmount)}`,
                        margin: [0, 0, 0, 4],
                      },
                      {
                        text: `Rupees ${amountInWords(payableAmount)} Only`,
                        fontSize: 10,
                      },
                    ],
                    margin: [8, 8, 8, 8],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => '#cccccc',
              vLineColor: () => '#cccccc',
            },
            margin: [0, 0, 0, 16],
          },

          {
            text: 'This is a system-generated payslip and does not require a signature.',
            alignment: 'center',
            fontSize: 9,
            color: '#666666',
            margin: [0, 8, 0, 0],
          },
        ],
      };

      pdfMake
        .createPdf(docDefinition)
        .download(
          `Payslip_${displayValue(payslip.employee_id, 'Unknown')}.pdf`,
        );
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Please try again.', {
        position: 'top-right',
        autoClose: 2000,
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const fetchPayslipDetails = async (row) => {
    if (row.source === 'FINAL_SETTLEMENT') {
      return httpInjectorService.getFinalSettlementPayslipDetails({
        month: row.Month,
        year: row.Year,
        users: row.user_id,
      });
    }

    return httpInjectorService.getPayslipDetails({
      month: row.Month,
      year: row.Year,
      users: row.user_id,
    });
  };

  // const handleView = async (userId) => {
  //   setViewUserId(userId);
  //   try {
  //     const response = await httpInjectorService.getFinalSettlementPayslipDetails({ month, year, users: userId });
  //     if (response.status === 'success') {
  //       setSelectedPayslip(response.data);
  //       setShow(true);
  //       setIsModalVisible(true);
  //     } else {
  //       toast.error(response?.message || 'Failed to fetch payslip details.', { position: 'top-right', autoClose: 2000 });
  //     }
  //   } catch (err) {
  //     console.error('Error fetching payslip details:', err);
  //     toast.error('Something went wrong while fetching payslip.', { position: 'top-right', autoClose: 2000 });
  //   }
  // };

  const handleView = async (row) => {
    setViewUserId(row.user_id);

    try {
      const response = await fetchPayslipDetails(row);

      if (response.status === 'success') {
        setSelectedPayslip(response.data?.[0] || response.data);
        setSelectedRow(row);
        setShow(true);
        setIsModalVisible(true);
      } else {
        toast.error(response?.message || 'Failed to fetch payslip details.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong while fetching payslip.');
    }
  };

  // const handleDownloadFromTable = async (userId) => {
  //   try {
  //     const response = await httpInjectorService.getFinalSettlementPayslipDetails({ month, year, users: [userId] });
  //     if (response.status === 'success') {
  //       const payslip = Array.isArray(response.data) ? response.data[0] : null;
  //       if (payslip) {
  //         await generatePayslipPDF(payslip);
  //       } else {
  //         toast.error('Payslip data is empty.', { position: 'top-right', autoClose: 2000 });
  //       }
  //     } else {
  //       toast.error(response?.message || 'Failed to fetch payslip details.', { position: 'top-right', autoClose: 2000 });
  //     }
  //   } catch (err) {
  //     console.error('Error fetching payslip details:', err);
  //     toast.error('Something went wrong while fetching payslip.', { position: 'top-right', autoClose: 2000 });
  //   }
  // };
  const handleDownloadFromTable = async (row) => {
    try {
      const response = await fetchPayslipDetails(row);

      console.log('DOWNLOAD PARAM', row);

      if (response.status === 'success') {
        const payslip = Array.isArray(response.data)
          ? response.data[0]
          : response.data;

        if (payslip) {
          await generatePayslipPDF(payslip);
        } else {
          toast.error('Payslip data is empty.');
        }
      } else {
        toast.error(response?.message || 'Failed to fetch payslip details.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong while fetching payslip.');
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedPayslip(null);
    setViewUserId(null);
    setShow(false);
  };

  const filteredData = useMemo(() => {
    const q = searchText.toLowerCase();
    return usersData.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.designation_name.toLowerCase().includes(q) ||
        u.department_name.toLowerCase().includes(q),
    );
  }, [usersData, searchText]);

  const renderPayslipModal = () => {
    if (!show || !selectedPayslip) return null;
    const payslip = Array.isArray(selectedPayslip)
      ? selectedPayslip[0]
      : selectedPayslip;
    if (!payslip) return null;

    const normalizedPayslip = {
      ...payslip,
      payslipMonth: payslip?.Month || payslip?.month,
      payslipYear: payslip?.Year || payslip?.year,
      payableAmount: getPayableAmount(payslip),
    };

    const totalEarnings = getPayableAmount(payslip);

    return (
      <Modal
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={720}
        style={{ top: 5 }}
        destroyOnClose
      >
        <div ref={payslipRef} style={{ background: '#fff', padding: '16px' }}>
          <img
            src={nubaxLogo}
            height="80"
            width="80"
            alt="Company Logo"
            style={{ display: 'block', margin: '0 auto' }}
          />

          <h2 style={{ textAlign: 'center', marginTop: 8 }}>
            {displayValue(payslip.company_name)}
          </h2>
          <p style={{ textAlign: 'center', margin: 0 }}>
            {displayValue(payslip.registered_office) ||
              displayValue(payslip.corporate_office)}
          </p>
          <h4 style={{ textAlign: 'center', marginTop: 12 }}>
            Payslip for the Month of{' '}
            {displayValue(normalizedPayslip.payslipMonth)},{' '}
            {displayValue(normalizedPayslip.payslipYear)}
          </h4>

          <Divider />

          <div
            style={{
              display: 'flex',
              border: '1px solid #e8e8e8',
              borderRadius: 8,
              overflow: 'hidden',
              marginBottom: 20,
            }}
          >
            <div style={{ flex: 1, padding: 16 }}>
              {[
                ['Name', payslip.employee_name],
                ['Designation', payslip.designations],
                ['Department', payslip.department_name],
                ['Location', payslip.work_location],
                ['LOP', payslip.LOP_Deduction_amount],
              ].map(([label, value], i) => (
                <Row className="mb-2" key={i}>
                  <Col span={10}>{label}:</Col>
                  <Col span={14}>{displayValue(value)}</Col>
                </Row>
              ))}
            </div>
            <div style={{ width: 1, backgroundColor: '#e8e8e8' }} />
            <div style={{ flex: 1, padding: 16 }}>
              {[
                ['Employee ID', payslip.employee_id],
                ['Bank Name', payslip.bank_name],
                ['Account No', payslip.account_number],
                ['PAN No', payslip.pan_no],
              ].map(([label, value], i) => (
                <Row className="mb-2" key={i}>
                  <Col span={10}>{label}:</Col>
                  <Col span={14}>{displayValue(value)}</Col>
                </Row>
              ))}
            </div>
          </div>

          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: 8,
              overflow: 'hidden',
              marginBottom: 20,
            }}
          >
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, padding: 16 }}>
                <div style={{ marginBottom: 8 }}>Earnings</div>
                <Divider style={{ margin: '8px 0' }} />
                {[
                  ['EL Encashment', payslip.EL_Encashment],
                  ['Basic', payslip.basic_month],
                  ['HRA', payslip.hra_month],
                  ['Conveyance Allowance', payslip.conveyance_allowance_month],
                  ['Special Allowance', payslip.special_allowance_month || 0],
                  ['Variable', payslip.variable],
                ].map(([label, value], i) => (
                  <Row className="mb-2" key={i}>
                    <Col span={14}>{label}</Col>
                    <Col span={10}>{formatCurrency(value)}</Col>
                  </Row>
                ))}
              </div>
              <div style={{ width: 1, backgroundColor: '#e8e8e8' }} />
              <div style={{ flex: 1, padding: 16 }}>
                <div style={{ marginBottom: 8 }}>Deductions</div>
                <Divider style={{ margin: '8px 0' }} />
                <Row className="mb-2">
                  <Col span={14}>Adoc</Col>
                  <Col span={10}>{formatCurrency(payslip.adoc_deduction)}</Col>
                </Row>
              </div>
            </div>
            <div style={{ height: 1, backgroundColor: '#e8e8e8' }} />
            <div style={{ padding: 16, backgroundColor: '#fafafa' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Row>
                    <Col span={14}>Total Earnings</Col>
                    <Col span={10}>{formatCurrency(totalEarnings)}</Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Row>
                    <Col span={14}>Total Deductions</Col>
                    <Col span={10}>
                      {formatCurrency(payslip.adoc_deduction)}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </div>

          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <Row className="mb-2">
              <Col span={8}>Net Pay For The Month</Col>
              <Col span={16}>
                {formatCurrency(normalizedPayslip.payableAmount)}
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                Rupees {amountInWords(normalizedPayslip.payableAmount)} Only
              </Col>
            </Row>
          </div>

          <Divider />
          <p style={{ textAlign: 'center', fontSize: 12, color: '#666' }}>
            This is a system-generated payslip and does not require a signature.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={pdfLoading}
            onClick={() => handleDownloadFromTable(selectedRow)}
          >
            Download PDF
          </Button>
        </div>
      </Modal>
    );
  };

  const columns = useMemo(
    () => [
      { Header: 'Sl. No', accessor: 'slno', Cell: ({ row }) => row.index + 1 },
      { Header: 'Name', accessor: 'name' },
      { Header: 'Designation', accessor: 'designation_name' },
      { Header: 'Department', accessor: 'department_name' },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <Space>
            <Tooltip title="View">
              <Button
                type="link"
                icon={<EyeOutlined style={{ fontSize: 18 }} />}
                onClick={() => handleView(row.original)}
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                type="link"
                icon={<DownloadOutlined style={{ fontSize: 18 }} />}
                loading={pdfLoading}
                onClick={() => handleDownloadFromTable(row.original)}
              />
            </Tooltip>
          </Space>
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    [pdfLoading],
  );

  return (
    <Card bordered={false}>
      <TableContainer
        columns={columns}
        data={filteredData}
        isGlobalFilter={true}
        customPageSize={10}
        className="custom-header-css"
      />
      {renderPayslipModal()}
    </Card>
  );
};

export default PayslipUsers;